import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

import common from '@/shared/i18n/en/common';
import { tr } from '@/shared/i18n/translate';

import { fmtShortDate, fmtWeekday } from './format-locale';

// Our typography scale adds the named steps micro/caption/footnote/body/title/headline/display
// (see globals.css @theme --text-*).  tailwind-merge doesn't know these are
// font sizes, so by default it treats `text-caption` etc. as text-*color*
// utilities and silently drops them whenever a real text-color is merged in
// the same cn() call (e.g. `cn('text-caption', 'text-[var(--accent)]')`),
// making the element fall back to the inherited 14px.  Register them in the
// font-size group so they merge correctly against colors and against each other.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['micro', 'caption', 'footnote', 'body', 'md', 'title', 'headline', 'display'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 1) return tr(common, 'justNow');
  if (minutes < 60) return tr(common, 'minutesAgoShort', { count: minutes });
  if (hours < 24) return tr(common, 'hoursAgoShort', { count: hours });
  if (days < 7) return tr(common, 'daysAgoShort', { count: days });
  // The UI language (never the browser locale), so the date matches the
  // relative words above.
  return fmtShortDate(d);
}

export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (diffDays === 0) return tr(common, 'today');
  if (diffDays === 1) return tr(common, 'tomorrow');
  if (diffDays === -1) return tr(common, 'yesterday');
  if (diffDays > 1 && diffDays <= 6) {
    return fmtWeekday(d);
  }
  if (diffDays >= -6 && diffDays < -1) {
    return tr(common, 'daysAgoShort', { count: Math.abs(diffDays) });
  }
  // The UI language (never the browser locale), like the words above.
  return fmtShortDate(d);
}

/**
 * Format a Date as a `YYYY-MM-DD` string using LOCAL calendar fields — unlike
 * `toISOString().slice(0,10)`, this never shifts the day across the UTC
 * boundary (e.g. an evening date in a UTC+ timezone).  Used to bridge our
 * `<DatePicker>` (returns Date) to date-only string state / form fields.
 */
export function toYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isOverdue(date: Date | string | null | undefined): boolean {
  if (!date) return false;
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.getTime() < Date.now();
}
